import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #pendingAdds = new Map();
  #previewObserver = null;
  #previewTimeout = null;
  #idleReady = false;
  #confirmedCount = null;
  #baseCount = 0;
  #baseQuantities = new Map();
  #baseTotalCents = 0;
  #baseTotalText = '';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('ps:cart-count-adjust', this.#adjustCount);
    document.addEventListener('ps:cart-count-confirmed', this.#confirmCount);
    document.addEventListener('ps:cart-pending-add', this.#onPendingAdd);
    document.addEventListener('ps:cart-sync-idle', this.#onSyncIdle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('ps:cart-count-adjust', this.#adjustCount);
    document.removeEventListener('ps:cart-count-confirmed', this.#confirmCount);
    document.removeEventListener('ps:cart-pending-add', this.#onPendingAdd);
    document.removeEventListener('ps:cart-sync-idle', this.#onSyncIdle);
    this.#previewObserver?.disconnect();
    clearTimeout(this.#previewTimeout);
  }

  #adjustCount = (event) => {
    const bubble = this.querySelector('.cart-drawer__heading .cart-bubble');
    if (!bubble) return;
    const previous = Number(bubble.dataset.cartCount);
    const delta = Number(event.detail?.delta);
    if (!Number.isFinite(previous) || !Number.isFinite(delta)) return;
    this.#renderCount(Math.max(0, previous + delta));
  };

  #confirmCount = (event) => {
    const count = Number(event.detail?.count);
    if (Number.isFinite(count)) {
      this.#renderCount(count);
      this.#confirmedCount = count;
      this.#maybeSettlePreview();
    }
  };

  #renderCount(count) {
    const bubble = this.querySelector('.cart-drawer__heading .cart-bubble');
    if (!bubble) return;
    bubble.dataset.cartCount = String(count);
    bubble.classList.toggle('visually-hidden', count === 0);
    bubble.toggleAttribute('data-maintain-ratio', count <= 99);

    const display = bubble.querySelector('.cart-bubble__text-count');
    if (display) {
      display.textContent = count < 100 ? String(count) : '';
      display.classList.toggle('hidden', count === 0);
    }
    const spoken = bubble.querySelector('.visually-hidden');
    if (spoken) spoken.textContent = spoken.textContent.replace(/:\s*\d+\s*$/, `: ${count}`);
  }

  #onPendingAdd = (event) => {
    const { variantId, title, image, price } = event.detail || {};
    if (!variantId) return;
    if (!this.#pendingAdds.size) {
      const rows = Array.from(this.querySelectorAll('cart-items-component tbody tr[data-key]'));
      this.#baseQuantities = new Map(rows.map((row) => [
        row.dataset.key?.split(':')[0],
        Number(row.querySelector('input[data-cart-line]')?.value || 0),
      ]));
      this.#baseCount = rows.reduce((count, row) =>
        count + Number(row.querySelector('input[data-cart-line]')?.value || 0), 0
      );
      this.#baseTotalText = this.querySelector('.cart-drawer__summary [data-cart-subtotal]')?.textContent?.trim() || '';
      this.#baseTotalCents = this.#moneyCents(this.#baseTotalText) || 0;
    }
    const item = this.#pendingAdds.get(String(variantId)) || { variantId: String(variantId), quantity: 0 };
    Object.assign(item, { title, image, price, priceCents: this.#moneyCents(price) });
    item.quantity += 1;
    this.#pendingAdds.set(String(variantId), item);
    this.#idleReady = false;
    this.#confirmedCount = null;
    if (this.querySelector('dialog')?.open) this.#showPendingCart();
  };

  #onSyncIdle = () => {
    if (!this.#pendingAdds.size) return;
    this.#idleReady = true;
    clearTimeout(this.#previewTimeout);
    // A failed section request still needs to release the temporary view.
    this.#previewTimeout = setTimeout(() => this.#clearPendingCart(), 5000);
    this.#maybeSettlePreview();
  };

  #maybeSettlePreview() {
    if (!this.#idleReady || this.#confirmedCount === null || window.psCartSync?.pending) return;
    const rows = this.querySelectorAll('cart-items-component tbody tr[data-key]');
    const renderedCount = Array.from(rows).reduce(
      (count, row) => count + Number(row.querySelector('input[data-cart-line]')?.value || 0), 0
    );
    if (renderedCount === this.#confirmedCount) this.#clearPendingCart();
  }

  #clearPendingCart() {
    this.querySelector('.ps-cart-preview')?.remove();
    this.querySelector('.ps-cart-preview__heading')?.remove();
    this.querySelector('.cart-drawer__header')?.classList.remove('ps-cart-preview-header');
    this.#previewObserver?.disconnect();
    this.#previewObserver = null;
    clearTimeout(this.#previewTimeout);
    this.#previewTimeout = null;
    this.#pendingAdds.clear();
    this.#idleReady = false;
    this.#confirmedCount = null;
    this.#baseCount = 0;
    this.#baseQuantities.clear();
    this.#baseTotalCents = 0;
    this.#baseTotalText = '';
  }

  #showPendingCart() {
    if (!this.#pendingAdds.size) return;
    const content = this.querySelector('.cart-drawer__content');
    if (!content) return;
    const committed = Array.from(this.querySelectorAll('cart-items-component tbody tr[data-key]')).reduce(
      (count, row) => count + Number(row.querySelector('input[data-cart-line]')?.value || 0), 0
    );
    const expected = this.#baseCount + Array.from(this.#pendingAdds.values()).reduce(
      (count, item) => count + item.quantity, 0
    );
    if (committed >= expected && !content.querySelector('.ps-cart-preview')) return;
    const dialog = this.querySelector('.cart-drawer__dialog');
    const header = this.querySelector('.cart-drawer__header');
    if (dialog?.classList.contains('cart-drawer--empty') && header && !header.querySelector('.ps-cart-preview__heading')) {
      const heading = document.createElement('span');
      heading.className = 'ps-cart-preview__heading';
      const label = document.createElement('span');
      label.textContent = this.dataset.cartLabel || 'Cart';
      const badge = document.createElement('span');
      badge.className = 'ps-cart-preview__badge';
      heading.append(label, badge);
      header.prepend(heading);
      header.classList.add('ps-cart-preview-header');
    }

    const existing = new Map();
    Array.from(this.querySelectorAll('cart-items-component tbody tr[data-key]')).reverse().forEach((row) => {
      if (row.classList.contains('removing') || row.classList.contains('awaiting-removal')) return;
      const id = row.dataset.key?.split(':')[0];
      if (!id) return;
      existing.set(id, {
        title: row.querySelector('.cart-items__title')?.textContent?.trim() || '',
        image: row.querySelector('.cart-items__media img')?.src || '',
        price: row.querySelector('.cart-items__price text-component')?.textContent?.trim() || '',
        quantity: Number(row.querySelector('input[data-cart-line]')?.value || 0),
      });
    });
    this.#pendingAdds.forEach((item, id) => {
      const previous = existing.get(id);
      const quantity = Math.max(previous?.quantity || 0, (this.#baseQuantities.get(id) || 0) + item.quantity);
      existing.set(id, {
        ...previous,
        ...item,
        quantity,
        price: item.priceCents == null ? (previous?.price || item.price) : this.#formatMoney(item.price, item.priceCents * quantity),
      });
    });

    let preview = content.querySelector('.ps-cart-preview');
    if (!preview) {
      preview = document.createElement('div');
      preview.className = 'ps-cart-preview';
      content.append(preview);
    }
    preview.replaceChildren();
    const list = document.createElement('div');
    list.className = 'ps-cart-preview__list cart-drawer__items';
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-items__wrapper';
    const items = document.createElement('div');
    items.className = 'cart-items';
    const table = document.createElement('table');
    table.className = 'cart-items__table';
    const body = document.createElement('tbody');
    table.append(body);
    items.append(table);
    wrapper.append(items);
    list.append(wrapper);
    let count = 0;
    existing.forEach((item) => {
      if (!item.quantity) return;
      count += item.quantity;
      const row = document.createElement('tr');
      row.className = 'cart-items__table-row ps-cart-preview__row';

      const media = document.createElement('td');
      media.className = 'cart-items__media';
      if (item.image) {
        const imageBox = document.createElement('span');
        imageBox.className = 'cart-items__media-container';
        const image = document.createElement('img');
        image.className = 'cart-items__media-image';
        image.src = item.image;
        image.alt = '';
        image.loading = 'eager';
        imageBox.append(image);
        media.append(imageBox);
      }

      const details = document.createElement('td');
      details.className = 'cart-items__details cart-primary-typography';
      const title = document.createElement('span');
      title.className = 'cart-items__title';
      title.textContent = item.title || '';
      details.append(title);

      const price = document.createElement('td');
      price.className = 'cart-items__price cart-secondary-typography';
      price.textContent = item.price || '';

      const quantity = document.createElement('td');
      quantity.className = 'cart-items__quantity';
      const selector = document.createElement('div');
      selector.className = 'ps-cart-preview__selector';
      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'quantity-minus';
      minus.textContent = '−';
      minus.disabled = true;
      const number = document.createElement('span');
      number.textContent = String(item.quantity);
      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'quantity-plus';
      plus.textContent = '+';
      plus.disabled = true;
      selector.append(minus, number, plus);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button button--tertiary cart-items__remove';
      remove.disabled = true;
      remove.setAttribute('aria-label', this.dataset.removeLabel || 'Remove');
      remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v6m4-6v6"/></svg>';
      quantity.append(selector, remove);
      row.append(media, details, quantity, price);
      body.append(row);
    });
    const badge = header?.querySelector('.ps-cart-preview__badge');
    if (badge) badge.textContent = String(count);

    const actualSummary = this.querySelector('.cart-drawer__summary');
    let summary;
    if (actualSummary) {
      summary = actualSummary.cloneNode(true);
      summary.querySelectorAll('button, input, a').forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) control.disabled = true;
        else control.removeAttribute('href');
      });
      summary.querySelectorAll('[id], [ref]').forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('ref');
      });
      const total = summary.querySelector('[data-cart-subtotal]');
      if (total) total.textContent = this.#predictedTotal();
      summary.querySelectorAll('shopify-accelerated-checkout-cart, .additional-checkout-buttons').forEach((element) => element.remove());
    } else {
      summary = document.createElement('div');
      summary.className = 'cart-drawer__summary ps-cart-preview__summary';
      const totals = document.createElement('div');
      totals.className = 'cart__summary-totals';
      const totalContainer = document.createElement('div');
      totalContainer.className = 'cart__total-container';
      const total = document.createElement('span');
      total.className = 'cart__summary-item cart__total';
      const label = document.createElement('span');
      label.className = 'cart__total-label cart-primary-typography';
      label.textContent = this.dataset.totalLabel || 'Estimated total';
      const pending = document.createElement('span');
      pending.className = 'cart__total-value cart-secondary-typography';
      pending.textContent = this.#predictedTotal();
      total.append(label, pending);
      totalContainer.append(total);
      totals.append(totalContainer);
      const ctas = document.createElement('div');
      ctas.className = 'cart__ctas';
      const checkout = document.createElement('button');
      checkout.type = 'button';
      checkout.className = 'cart__checkout-button button';
      checkout.textContent = this.dataset.checkoutLabel || 'Check out';
      checkout.disabled = true;
      ctas.append(checkout);
      summary.append(totals, ctas);
    }
    const previewCheckout = summary.querySelector('.cart__checkout-button');
    if (previewCheckout) {
      previewCheckout.disabled = false;
      previewCheckout.type = 'button';
      previewCheckout.removeAttribute('form');
      previewCheckout.addEventListener('click', async () => {
        previewCheckout.disabled = true;
        await window.psCartSync?.whenIdle();
        await this.#waitForCart();
        this.querySelector('.cart-drawer__summary .cart__checkout-button')?.click();
      });
    }
    preview.append(list, summary);

    if (!this.#previewObserver) {
      const cart = this.querySelector('cart-items-component');
      this.#previewObserver = new MutationObserver(() => this.#maybeSettlePreview());
      if (cart) this.#previewObserver.observe(cart, { childList: true, subtree: true });
    }
  }

  #moneyCents(value) {
    const amount = String(value || '').match(/\d[\d,]*(?:\.\d{1,2})?/);
    return amount ? Math.round(Number(amount[0].replace(/,/g, '')) * 100) : null;
  }

  #formatMoney(template, cents) {
    const value = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
    return template && /\d[\d,.]*/.test(template)
      ? template.replace(/\d[\d,.]*/, value)
      : `${value} ${window.Shopify?.currency?.active || 'USD'}`;
  }

  #predictedTotal() {
    const additions = Array.from(this.#pendingAdds.values()).reduce(
      (cents, item) => cents + (item.priceCents || 0) * item.quantity, 0
    );
    return this.#formatMoney(this.#baseTotalText, this.#baseTotalCents + additions);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) this.open();
  };

  #cartIsReady() {
    if (window.psCartSync?.pending || this.#pendingAdds.size) return false;
    const rows = Array.from(this.querySelectorAll('cart-items-component tbody tr[data-key]'));
    const rendered = rows.reduce(
      (count, row) => count + Number(row.querySelector('input[data-cart-line]')?.value || 0), 0
    );
    const bubble = this.querySelector('.cart-drawer__heading .cart-bubble');
    const expected = Number(bubble?.dataset.cartCount || 0);
    const total = this.querySelector('.cart-drawer__summary [data-cart-subtotal]');
    return rendered === expected && (!rendered || (total && total.textContent.trim() !== '…'));
  }

  #waitForCart() {
    return new Promise((resolve) => {
      const check = () => {
        this.#maybeSettlePreview();
        if (!this.#cartIsReady()) return;
        clearInterval(poll);
        resolve();
      };
      const poll = setInterval(check, 80);
      check();
    });
  }

  open() {
    this.showDialog();
    this.#showPendingCart();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

